import React, { useState } from "react";
import {
  KeyIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Alert from "../components/Alert";
import SecurityNotice from "../components/SecurityNotice";
import ActionButton from "../components/ActionButton";
import SEO from "../components/SEO";
import { downloadFile } from "../utils/helpers";
import { generateStandardFilename } from "../../shared/utils/fileHelpers";
import { useScrollToResults } from "../hooks/useScrollToResults";
import { useCopyFeedback } from "../hooks/useCopyFeedback";
// 2026-07-04 tools-wide overhaul (operator-approved), audit item 4 :
// dropped the unused generatePassword import ; this page only ever
// generates via generateMultiplePasswords (count 1 covers the single
// password case), so the extra import was dead weight flagged by the
// no-unused-vars lint probe.
import {
  generateMultiplePasswords,
  calculateEntropy,
  getStrengthRating,
  PASSWORD_CONFIG,
} from "../utils/passwordGenerator";

function PasswordGenerator() {
  const [length, setLength] = useState(PASSWORD_CONFIG.DEFAULT_LENGTH);
  const [options, setOptions] = useState({
    useUppercase: true,
    useLowercase: true,
    useNumbers: true,
    useSymbols: true,
    excludeAmbiguous: false,
  });
  const [passwordCount, setPasswordCount] = useState(1);
  const [passwords, setPasswords] = useState([]);
  // 2026-07-04 tools-wide overhaul (operator-approved), audit item 12 :
  // state-driven transient "Copied!" feedback (the CSRGenerator
  // handleCopy pattern). Keyed by password index (or "all" for the
  // Copy Everything button) so only the clicked control shows the
  // confirmation.
  const { copyStatus, handleCopy, resetCopyStatus } = useCopyFeedback();
  const [error, setError] = useState(null);
  // Browser support, decided once at mount by the state initialiser rather
  // than in an effect : it cannot change while mounted, and setting it from
  // an effect painted a supported page first (react-hooks/set-state-in-effect).
  const [browserSupported] = useState(() =>
    Boolean(window.crypto && window.crypto.getRandomValues)
  );
  const resultsRef = useScrollToResults(
    passwords.length > 0 ? passwords : null
  );

  const handleGenerate = (e) => {
    if (e) e.preventDefault();
    setError(null);
    // Stale copy feedback goes with the result it belonged to
    // (positional keys would otherwise light up the NEW result's row
    // inside the 2-second window).
    resetCopyStatus();

    // Validation
    if (
      !options.useUppercase &&
      !options.useLowercase &&
      !options.useNumbers &&
      !options.useSymbols
    ) {
      setError("Select at least one character type.");
      return;
    }

    if (
      length < PASSWORD_CONFIG.MIN_LENGTH ||
      length > PASSWORD_CONFIG.MAX_LENGTH
    ) {
      setError(
        `The password length must be between ${PASSWORD_CONFIG.MIN_LENGTH} and ${PASSWORD_CONFIG.MAX_LENGTH} characters.`
      );
      return;
    }

    try {
      const generatedPasswords = generateMultiplePasswords(passwordCount, {
        length,
        ...options,
      });

      const passwordsWithMetadata = generatedPasswords.map((password) => {
        const entropy = calculateEntropy(password, options);
        const strength = getStrengthRating(entropy);
        return {
          value: password,
          entropy,
          strength,
        };
      });

      setPasswords(passwordsWithMetadata);
    } catch (err) {
      setError(err.message || "The passwords could not be generated.");
    }
  };

  const handleReset = () => {
    setLength(PASSWORD_CONFIG.DEFAULT_LENGTH);
    setOptions({
      useUppercase: true,
      useLowercase: true,
      useNumbers: true,
      useSymbols: true,
      excludeAmbiguous: false,
    });
    setPasswordCount(PASSWORD_CONFIG.MIN_COUNT);
    setPasswords([]);
    setError(null);
    // Drop any in-flight "Copied!" feedback with the results it
    // belonged to (the sibling pages' reset behavior ; this page
    // historically skipped it because the unmounting results card hid
    // the stale state anyway).
    resetCopyStatus();
  };

  /**
   * Copy a single generated password to the clipboard.
   *
   * 2026-07-04 tools-wide overhaul (operator-approved), audit item 12 :
   * this copy used to be silent, which the audit flagged against the
   * house standard (state-driven transient "Copied!" ; no silent
   * copies). The status is only set when copyToClipboard reports
   * success, so a failed copy never shows a false confirmation, and it
   * auto-clears after 2 seconds to match CSRGenerator handleCopy.
   */
  const handleCopyPassword = (password, index) => handleCopy(password, index);

  // Copy every generated password (newline-separated) ; same transient
  // success-gated feedback as handleCopyPassword above, keyed "all".
  const handleCopyAll = () =>
    handleCopy(passwords.map((p) => p.value).join("\n"), "all");

  // 2026-08-07 download/copy consistency pass (operator-directed) :
  // this download (with the per-certificate download in
  // CertificateChainDiagram.jsx:downloadCertificate) bypassed the
  // shared downloadFile helper with a hand-rolled hidden-anchor
  // click, and alone in the suite it also bypassed the standard
  // {identifier}-{type}-{timestamp} filename
  // (docs/FILENAME_STANDARDS.md) : it saved an epoch-millis
  // `passwords-<Date.now()>.txt`. Passwords have no domain or Common
  // Name to identify them, so the identifier is the neutral
  // "generated".
  const handleDownload = () => {
    const content = passwords
      .map(
        (p, i) =>
          `Password ${i + 1} : ${p.value}\nStrength : ${p.strength.label} (${p.entropy} bits)\n`
      )
      .join("\n");

    downloadFile(
      content,
      generateStandardFilename("generated", "passwords", "txt")
    );
  };

  const handleOptionChange = (option) => {
    setOptions((prev) => ({ ...prev, [option]: !prev[option] }));
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 2026-07-04 tools-wide overhaul (operator-approved) : canonicalUrl
          added so search engines index the tool at its one true address. */}
      <SEO
        title="Strong Password Generator - Create Secure Random Passwords"
        description="Generate strong, random passwords with customizable length and character types. Free online password generator with strength meter and entropy calculation. Create secure passwords instantly."
        keywords="password generator, strong password, random password generator, secure password, password strength, password creator, generate password, free password tool"
        canonicalUrl="https://tools.trustico.com/password"
      />

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-heading mb-4 text-center">
          Strong Password Generator
        </h1>
        <p className="text-body">
          Generate strong random passwords with the length and character types
          you choose. Every password is created in your browser with a
          cryptographically secure random number generator, and the strength
          meter shows how hard each result would be to guess. Options include
          excluding characters that look alike and generating several passwords
          at once, and every result can be copied or downloaded. Longer is
          stronger, and sixteen characters or more is a good starting point for
          anything important.
        </p>
      </div>

      {/* A genuine error condition (the tool cannot run at all), so it
          renders in the error Alert. The text is a plain sentence, like
          every customer-facing error since the 2026-08-27 ruling. */}
      {!browserSupported && (
        <Alert type="error" title="Browser Not Supported" className="mb-6">
          Your browser does not support secure password generation. Please use a
          modern browser such as Chrome, Firefox, Safari or Edge to use this
          tool.
        </Alert>
      )}

      <SecurityNotice />

      <form onSubmit={handleGenerate} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Password Options */}
          <div className="space-y-6">
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-heading mb-2">
                Password Length
              </h2>
              <p className="text-body mb-4">
                Choose the length of your password.
              </p>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-link">{length}</span>
                  <span className="text-muted">characters</span>
                </div>
                <input
                  type="range"
                  min={PASSWORD_CONFIG.MIN_LENGTH}
                  max={PASSWORD_CONFIG.MAX_LENGTH}
                  value={length}
                  onChange={(e) => setLength(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 dark:bg-control rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-muted">
                  <span>{PASSWORD_CONFIG.MIN_LENGTH}</span>
                  <span>{PASSWORD_CONFIG.MAX_LENGTH}</span>
                </div>
              </div>
            </div>

            <div className="card p-6">
              <h2 className="text-lg font-semibold text-heading mb-2">
                Character Types
              </h2>
              <p className="text-body mb-4">
                Select which types of characters to include in your password.
                Using multiple character types significantly increases password
                strength.
              </p>
              <div className="space-y-3">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.useUppercase}
                    onChange={() => handleOptionChange("useUppercase")}
                    className="h-4 w-4 text-link focus:ring-blue-500 border-control rounded-sm"
                  />
                  <span className="ml-3 text-heading">
                    <span className="font-medium">Uppercase Letters</span>
                    <span className="text-muted ml-2">(A-Z)</span>
                  </span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.useLowercase}
                    onChange={() => handleOptionChange("useLowercase")}
                    className="h-4 w-4 text-link focus:ring-blue-500 border-control rounded-sm"
                  />
                  <span className="ml-3 text-heading">
                    <span className="font-medium">Lowercase Letters</span>
                    <span className="text-muted ml-2">(a-z)</span>
                  </span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.useNumbers}
                    onChange={() => handleOptionChange("useNumbers")}
                    className="h-4 w-4 text-link focus:ring-blue-500 border-control rounded-sm"
                  />
                  <span className="ml-3 text-heading">
                    <span className="font-medium">Numbers</span>
                    <span className="text-muted ml-2">(0-9)</span>
                  </span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.useSymbols}
                    onChange={() => handleOptionChange("useSymbols")}
                    className="h-4 w-4 text-link focus:ring-blue-500 border-control rounded-sm"
                  />
                  <span className="ml-3 text-heading">
                    <span className="font-medium">Symbols</span>
                    <span className="text-muted ml-2">(!@#$%^&*)</span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Right Column - Advanced Options */}
          <div className="space-y-6">
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-heading mb-2">
                Advanced Options
              </h2>
              <p className="text-body mb-4">
                Configure additional password generation options for enhanced
                customization and usability.
              </p>
              <div className="space-y-4">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.excludeAmbiguous}
                    onChange={() => handleOptionChange("excludeAmbiguous")}
                    className="h-4 w-4 text-link focus:ring-blue-500 border-control rounded-sm mt-1"
                  />
                  <div className="ml-3">
                    <span className="font-medium text-heading">
                      Exclude Ambiguous Characters
                    </span>
                    <p className="text-body mt-1">
                      Excludes characters that can be easily confused, such as 0
                      (zero), O (uppercase o), l (lowercase L), and I (uppercase
                      i). This is useful when passwords need to be typed
                      manually.
                    </p>
                  </div>
                </label>
              </div>
            </div>

            <div className="card p-6">
              <h2 className="text-lg font-semibold text-heading mb-2">
                Number of Passwords
              </h2>
              <p className="text-body mb-4">
                Generate multiple passwords at once. Useful when you need to
                create passwords for multiple accounts or users.
              </p>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-link">
                    {passwordCount}
                  </span>
                  <span className="text-muted">
                    {passwordCount === 1 ? "password" : "passwords"}
                  </span>
                </div>
                <input
                  type="range"
                  min={PASSWORD_CONFIG.MIN_COUNT}
                  max={PASSWORD_CONFIG.MAX_COUNT}
                  value={passwordCount}
                  onChange={(e) => setPasswordCount(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 dark:bg-control rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-muted">
                  <span>{PASSWORD_CONFIG.MIN_COUNT}</span>
                  <span>{PASSWORD_CONFIG.MAX_COUNT}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
            {/* 2026-07-04 operator consistency directive : the primary
                action is disabled until the page's one required input
                condition is met (at least one character type selected),
                alongside the existing browser-support gate. The
                handleGenerate guard keeps the error sentence as the
                direct-form-submission backstop, matching the gate +
                guard pattern on ChainAnalyzer and CertificateMatcher. */}
            <ActionButton
              type="submit"
              variant="primary"
              icon={KeyIcon}
              disabled={
                !browserSupported ||
                (!options.useUppercase &&
                  !options.useLowercase &&
                  !options.useNumbers &&
                  !options.useSymbols)
              }
            >
              Generate Password{passwordCount > 1 ? "s" : ""}
            </ActionButton>
            <ActionButton
              type="button"
              onClick={handleReset}
              variant="outline"
              icon={XMarkIcon}
              disabled={!browserSupported}
            >
              {/* 2026-07-04 operator consistency directive : reset
                  buttons across every tool page say "Reset" (the
                  previous label was "Clear Data"). */}
              Reset
            </ActionButton>
          </div>
        </div>
      </form>

      {error && (
        <Alert type="error" className="mt-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {passwords.length > 0 && (
        <div ref={resultsRef} className="mt-8 animate-fade-in">
          <div className="card p-6">
            <div className="flex justify-between items-center mb-2">
              {/* 2026-07-04 operator consistency directive : recased
                  from h2 text-2xl font-bold to the reference section
                  heading size (text-lg font-semibold) - the h1 page
                  title is the only larger heading a tool page carries. */}
              <h2 className="text-lg font-semibold text-heading">
                Generated Password{passwords.length > 1 ? "s" : ""}
              </h2>
              {/* flex-wrap so the fixed-min-width pair stacks instead
                  of overflowing on narrow screens - the CAA pages'
                  header pairs already carry it (same defect class the
                  operator reported on /chain, 2026-08-07). */}
              <div className="flex flex-wrap gap-3">
                <ActionButton
                  variant="outline"
                  onClick={handleCopyAll}
                  icon={ClipboardDocumentIcon}
                >
                  {copyStatus.all ? "Copied!" : "Copy Everything"}
                </ActionButton>
                {/* 2026-08-07 (operator-directed) : Download used to
                    render only for a multi-password batch
                    (passwords.length > 1). The gate is removed : a
                    single password is a savable artifact like any
                    other, and every other tool offers its Download
                    whenever a result exists. */}
                <ActionButton
                  variant="outline"
                  onClick={handleDownload}
                  icon={ArrowDownTrayIcon}
                >
                  Download
                </ActionButton>
              </div>
            </div>

            {/* The plain-text sentence is an operator decision
                (2026-08-07) : with Download offered for every result,
                the customer is told what the saved file contains and
                what to do with it, in the page's existing guidance
                paragraph rather than a separate warning banner. */}
            <p className="text-body mb-4">
              Your secure passwords have been generated using cryptographically
              secure random number generation. Each password includes a strength
              indicator based on entropy calculation. Downloaded password files
              are saved in plain text : delete the file once your passwords are
              stored somewhere safe.
            </p>

            <div className="space-y-4">
              {passwords.map((passwordData, index) => (
                <div
                  key={index}
                  className="bg-gray-50 dark:bg-gray-900/50 p-5 rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <p className="text-xs font-medium text-muted uppercase tracking-wider mb-2">
                        Password {index + 1}
                      </p>
                      <code className="font-mono text-heading break-all">
                        {passwordData.value}
                      </code>
                    </div>
                    {/* 2026-07-04 tools-wide overhaul (operator-approved),
                        audit item 12 : icon-only copy button now flips to a
                        green check with a "Copied!" label while copyStatus
                        holds this row's index, mirroring the ApiDocs
                        endpoint-copy treatment of the same pattern. */}
                    <button
                      type="button"
                      onClick={() =>
                        handleCopyPassword(passwordData.value, index)
                      }
                      className="ml-4 p-2 text-muted hover:text-body transition-colors"
                      title={
                        copyStatus[index] ? "Copied!" : "Copy to Clipboard"
                      }
                      aria-label={
                        /* Indexed so screen readers can tell the
                           rows' otherwise identically-named copy
                           buttons apart ; the visual tooltip (title)
                           keeps the generic wording. */
                        copyStatus[index]
                          ? "Copied!"
                          : `Copy Password ${index + 1}`
                      }
                    >
                      {copyStatus[index] ? (
                        <CheckIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                      ) : (
                        <ClipboardDocumentIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  {/* Strength Meter.
                      2026-07-04 tools-wide audit Tier 1 : the label and
                      bar classes used to be built dynamically as
                      `text-${color}-600` / `bg-${color}-500`. Tailwind's
                      JIT only emits classes it sees as LITERAL strings,
                      so every strength color was purged from production
                      CSS and the meter rendered colorless. The literal
                      maps below (keyed by the color names
                      calculateStrength in src/client/utils/passwordGenerator.js
                      returns : red / orange / yellow / lime / green)
                      make each class visible to the JIT. Do NOT
                      refactor back into interpolation ; a new strength
                      tier needs its literal entries added here. */}
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-body">Strength :</span>
                      <span
                        className={`font-semibold ${
                          {
                            red: "text-red-600 dark:text-red-300",
                            orange: "text-orange-700 dark:text-orange-400",
                            yellow: "text-yellow-700 dark:text-yellow-400",
                            lime: "text-lime-700 dark:text-lime-400",
                            green: "text-green-700 dark:text-green-400",
                          }[passwordData.strength.color] || "text-body"
                        }`}
                      >
                        {passwordData.strength.label}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 dark:inset-ring-1 dark:inset-ring-control rounded-full h-2">
                      <div
                        className={`${
                          {
                            red: "bg-red-500",
                            orange: "bg-orange-500",
                            yellow: "bg-yellow-500",
                            lime: "bg-lime-500",
                            green: "bg-green-500",
                          }[passwordData.strength.color] || "bg-gray-500"
                        } h-2 rounded-full transition-all duration-500`}
                        style={{ width: `${passwordData.strength.percent}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-muted">
                      <span>Entropy : {passwordData.entropy} bits</span>
                      <span>
                        Length : {passwordData.value.length} characters
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 card p-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              Understanding Password Strength
            </h2>
            <div className="space-y-3 text-body">
              <p>
                Password strength is measured using entropy, which represents
                the unpredictability of your password. Higher entropy means a
                more secure password that is harder to crack through brute-force
                attacks.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="bg-red-50 dark:bg-red-900/30 p-4 rounded-lg border border-red-200 dark:border-red-800/50">
                  <p className="font-semibold text-red-800 dark:text-red-200 mb-1">
                    Weak (&lt;40 bits)
                  </p>
                  <p className="text-red-700 dark:text-red-300">
                    Easily cracked. Not recommended for any account.
                  </p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/30 p-4 rounded-lg border border-orange-200 dark:border-orange-800/50">
                  <p className="font-semibold text-orange-800 dark:text-orange-200 mb-1">
                    Fair (40-59 bits)
                  </p>
                  <p className="text-orange-700 dark:text-orange-300">
                    Basic protection. Use only for low-value accounts.
                  </p>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/30 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800/50">
                  <p className="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
                    Good (60-79 bits)
                  </p>
                  <p className="text-yellow-700 dark:text-yellow-300">
                    Reasonable protection for most standard accounts.
                  </p>
                </div>
                <div className="bg-lime-50 dark:bg-lime-900/30 p-4 rounded-lg border border-lime-200 dark:border-lime-800/50">
                  <p className="font-semibold text-lime-800 dark:text-lime-200 mb-1">
                    Strong (80-99 bits)
                  </p>
                  <p className="text-lime-700 dark:text-lime-300">
                    Strong protection suitable for important accounts.
                  </p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg border border-green-200 dark:border-green-800/50 md:col-span-2">
                  <p className="font-semibold text-green-800 dark:text-green-200 mb-1">
                    Very Strong (100+ bits)
                  </p>
                  <p className="text-green-700 dark:text-green-300">
                    Excellent protection. Recommended for sensitive accounts
                    like banking, e-mail, and work systems.
                  </p>
                </div>
              </div>
              <p className="mt-4">
                Remember that even the strongest password can be compromised if
                reused across multiple sites. Always use unique passwords for
                each account and consider using a password manager to store them
                securely.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PasswordGenerator;
